/**
 * Brief documents (2026-10-15): every written document on a creative, whatever kind it
 * was uploaded as (a reference, a brief document, a static), listed where the team reads
 * the brief and the script, each opening in place. A Word file is read on the page
 * (converted on the server), a PDF in the PDF viewer, text as text; a .doc, .odt or .rtf
 * the server cannot convert opens to a card that says so, with its download. Nothing is
 * hidden: the same files stay in the Assets panel too.
 *
 * MS-10-2026-011 is why: four Word briefs, one per photo group, sat as downloads among
 * the photos and nobody read them in the app.
 */
import { useEffect, useMemo, useState } from 'react';
import { FileName, FolderPath } from '@/components/ui/FileName';
import { useQuery } from '@tanstack/react-query';
import { FileText, Folder, X, Download } from 'lucide-react';
import { fetchAssets, type Creative, type MediaAsset } from '@/lib/queries';
import { isBriefDocument, typeBadge } from '@/lib/file-types';
import FileViewer from '@/components/creative/FileViewer';
import { cn } from '@/lib/cn';

/** The creative's documents, brief documents first, then by folder and name. */
export function useBriefDocuments(creative: Pick<Creative, 'id'>) {
  const q = useQuery({ queryKey: ['assets', creative.id], queryFn: () => fetchAssets(creative.id) });
  const docs = useMemo(() => {
    const order = (a: MediaAsset) => (a.kind === 'brief_doc' ? 0 : a.kind === 'reference' ? 1 : 2);
    return (q.data ?? []).filter((a) => isBriefDocument(a))
      .sort((a, b) => order(a) - order(b)
        || (a.category ?? '').localeCompare(b.category ?? '', undefined, { numeric: true })
        || a.filename.localeCompare(b.filename, undefined, { numeric: true }));
  }, [q.data]);
  return { docs, isLoading: q.isLoading };
}

export default function BriefDocuments({ creative, compact = false }: { creative: Pick<Creative, 'id'>; compact?: boolean }) {
  const { docs } = useBriefDocuments(creative);
  const [open, setOpen] = useState<MediaAsset | null>(null);
  if (!docs.length) return null;
  return (
    <div className={cn(compact ? 'mt-3' : 'mt-4 pt-3 border-t border-slate-800/70')} data-testid="brief-documents">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted mb-2">
        <FileText size={12} /> Brief documents <span className="text-muted-strong normal-case tracking-normal">{docs.length}</span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {docs.map((a) => (
          <li key={a.id}>
            <button type="button" onClick={() => setOpen(a)} data-testid="brief-doc"
              className="w-full flex items-center gap-2.5 text-left rounded-lg border border-slate-800 hover:border-accent-400/50 bg-bg/40 px-2.5 py-2 transition">
              <span className="shrink-0 text-[9px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-white/85">{typeBadge(a)}</span>
              <span className="min-w-0 flex-1">
                <FileName name={a.filename} className="block text-xs text-muted-strong truncate" />
                <span className="flex items-center gap-1 text-[10px] text-muted truncate">
                  {a.kind.replace('_', ' ')}
                  {a.category && a.category.toLowerCase() !== 'any' && !['rough', 'colour', 'final'].includes(a.category) && (
                    <><span>·</span><Folder size={10} className="shrink-0" /><FolderPath path={a.category} className="truncate" /></>
                  )}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {open && <DocReaderDialog asset={docs.find((d) => d.id === open.id) ?? open} docs={docs} onNavigate={setOpen} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** One document, opened over the page. Escape closes; the arrows walk the list. */
function DocReaderDialog({ asset, docs, onNavigate, onClose }: { asset: MediaAsset; docs: MediaAsset[]; onNavigate: (a: MediaAsset) => void; onClose: () => void }) {
  const i = docs.findIndex((d) => d.id === asset.id);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && i >= 0 && i < docs.length - 1) onNavigate(docs[i + 1]);
      if (e.key === 'ArrowLeft' && i > 0) onNavigate(docs[i - 1]);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [i, docs, onNavigate, onClose]);
  const download = asset.attachment_url || asset.download_url;
  return (
    // above the "Viewing as" banner (z-80), which hid this header and its Close (2026-11-01)
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex flex-col" onClick={onClose} data-testid="doc-reader-dialog" role="dialog" aria-modal="true" aria-label={asset.filename}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-bg-card" onClick={(e) => e.stopPropagation()}>
        <FileText size={16} className="text-accent-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <FileName name={asset.filename} className="block text-sm text-muted-strong truncate" />
          <div className="text-[11px] text-muted truncate">
            {asset.kind.replace('_', ' ')}{asset.category && asset.category.toLowerCase() !== 'any' ? <> · <FolderPath path={asset.category} /></> : ''}
            {docs.length > 1 && i >= 0 ? ` · ${i + 1} of ${docs.length}` : ''}
          </div>
        </div>
        {docs.length > 1 && (
          <div className="hidden sm:flex items-center gap-1">
            <button type="button" disabled={i <= 0} onClick={() => onNavigate(docs[i - 1])} className="text-xs px-2 py-1 rounded border border-slate-700 text-muted hover:text-muted-strong disabled:opacity-40">Previous</button>
            <button type="button" disabled={i >= docs.length - 1} onClick={() => onNavigate(docs[i + 1])} className="text-xs px-2 py-1 rounded border border-slate-700 text-muted hover:text-muted-strong disabled:opacity-40">Next</button>
          </div>
        )}
        {download && (
          <a href={download} className="inline-flex items-center gap-1 text-xs text-accent-400 hover:underline shrink-0" data-testid="doc-reader-download"><Download size={13} /> <span className="hidden sm:inline">Download</span></a>
        )}
        <button type="button" onClick={onClose} aria-label="Close" className="text-muted hover:text-muted-strong shrink-0" data-testid="doc-reader-close"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-auto flex items-start justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <FileViewer key={asset.id} asset={asset} />
      </div>
    </div>
  );
}
